import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

/**
 * Role-Based Access Control Route Guard
 * Single-Login RBAC Model - Enforces role-based route access
 * 
 * IMPORTANT: This guard does NOT show a loading screen.
 * The GlobalAuthGate handles the "checking" state at the top level.
 * This guard only handles redirects for unauthenticated or wrong-role users.
 * 
 * @param {string[]} allowedRoles - Array of roles allowed to access this route (e.g., ['admin'])
 * @param {boolean} requireSeat - Whether an active seat is required (for member/viewer routes)
 * @param {React.ReactNode} children - The component to render if authorized
 */
const RoleProtectedRoute = ({ allowedRoles = [], requireSeat = false, children }) => {
  const { user, authStatus } = useAuth();
  const location = useLocation();

  // Note: authStatus === 'checking' is handled by GlobalAuthGate
  // We only reach here when auth check is complete

  // Not authenticated - redirect to login
  if (authStatus === 'unauthenticated' || !user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  const userRole = user.role || 'member';
  const seatActive = user.seat_active || false;

  // Check if user is an admin (admin or primary_admin)
  const isAdmin = userRole === 'admin' || userRole === 'primary_admin';

  // Check if user's role is in allowed roles
  const isRoleAllowed = allowedRoles.length === 0 || allowedRoles.includes(userRole);

  // If role not allowed, redirect based on role
  if (!isRoleAllowed) {
    // Admins trying to access member-only routes (unlikely but handle it)
    if (isAdmin) {
      return <Navigate to="/partner-pro/dashboard" replace />;
    }
    
    // Members/Viewers trying to access admin routes
    return <Navigate to="/partner-pro/workspace" replace />;
  }

  // If seat is required and user doesn't have one
  // Still allow access but the page should show seat-required UI
  // This is for UX - backend will enforce seat requirement on actions
  if (requireSeat && !seatActive && !isAdmin) {
    // Allow access but page will show seat requirement banner
    // Don't block navigation, just limit actions
  }

  // User is authorized
  return <>{children}</>;
};

export default RoleProtectedRoute;
